import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ScanFace,
  Download,
  Calendar as CalendarIcon,
  Search,
  MapPin,
  CheckCircle2,
  CircleSlash,
  AlertTriangle,
  Filter,
  Clock,
  ShieldCheck,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { fmtTime } from "@/lib/utils-app";
import { MapContainer, TileLayer, Circle, CircleMarker, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

function todayIsoIST() {
  // ISO date string in IST timezone (yyyy-mm-dd)
  const now = new Date();
  const ist = new Date(now.getTime() + (330 + now.getTimezoneOffset()) * 60_000);
  return ist.toISOString().slice(0, 10);
}

/**
 * Admin "ITCOT Hourly Attendance" page.
 *
 * Each row = one PMC supervisor. Columns are the visible working hours from
 * AppSettings (default 08:00..18:00). Each cell shows that hour's selfies
 * (thumbnails) with timestamp; multiple captures in the same hour stack.
 * Empty cells render a soft "—" so missed slots are obvious at a glance.
 */
export default function AdminHourlySelfiePage() {
  const [date, setDate] = useState(todayIsoIST());
  const [parkId, setParkId] = useState("all");
  const [parks, setParks] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // mapView holds the supervisor row whose full day-of-captures we want
  // to render on a Leaflet map (with park geofence circles) plus a
  // chronological side panel. Set when the user clicks any thumbnail OR
  // the supervisor name. `focusId` highlights one capture in the list
  // and auto-pans the map to it.
  const [mapView, setMapView] = useState(null); // { row, focusId }

  // Bootstrap park list
  useEffect(() => {
    api.get("/parks", { params: { active: true } }).then((r) => setParks(r.data || []));
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get("/admin/hourly-selfie/grid", {
        params: { date, ...(parkId !== "all" ? { park_id: parkId } : {}) },
      })
      .then((r) => {
        if (!alive) return;
        setData(r.data);
      })
      .catch(() => alive && toast.error("Failed to load hourly grid"))
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });
    return () => { alive = false; };
  }, [date, parkId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const hourRange = useMemo(() => {
    if (!data) return [];
    const ws = parseInt((data.window_start || "08:00").split(":")[0], 10);
    const we = parseInt((data.window_end || "18:00").split(":")[0], 10);
    const start = isNaN(ws) ? 8 : ws;
    const end = isNaN(we) ? 18 : we;
    const arr = [];
    for (let h = start; h < end; h++) arr.push(h);
    return arr;
  }, [data]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.supervisors;
    return data.supervisors.filter((r) =>
      `${r.name || ""} ${r.username || ""} ${(r.park_names || []).join(" ")}`
        .toLowerCase()
        .includes(q),
    );
  }, [data, search]);

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/hourly-selfie/export.xlsx", {
        params: { date, ...(parkId !== "all" ? { park_id: parkId } : {}) },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `hourly-selfie-${date}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const summary = data?.summary || {};
  const enabled = data?.enabled !== false;

  return (
    <AppShell
      title="ITCOT Hourly Attendance"
      subtitle="Per-hour self check-in selfies captured by PMC supervisors"
      action={
        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant="outline"
            onClick={exportXlsx}
            data-testid="hourly-export-xlsx"
          >
            <Download className="mr-1 h-4 w-4" /> Export XLSX
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {!enabled ? (
          <Card className="border-amber-300 bg-amber-50/70">
            <CardContent className="flex items-center gap-2 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              Hourly self-attendance is currently <strong>disabled</strong> in Settings. Existing captures still display below.
            </CardContent>
          </Card>
        ) : null}

        {/* Filters */}
        <Card>
          <CardContent className="flex flex-wrap items-end gap-2 p-3">
            <div className="flex flex-col">
              <label className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Date</label>
              <div className="relative">
                <CalendarIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="h-9 w-[160px] pl-7"
                  data-testid="hourly-date"
                />
              </div>
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Park</label>
              <Select value={parkId} onValueChange={setParkId}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="hourly-park">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All parks</SelectItem>
                  {parks.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.code} · {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex flex-col">
              <label className="mb-1 text-[11px] uppercase tracking-wide text-slate-500">Search supervisor</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Name, username or park"
                  className="h-9 w-[260px] pl-7"
                  data-testid="hourly-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile label="Supervisors" v={summary.total_supervisors} icon={ScanFace} tone="slate" />
          <Tile label="Active today" v={summary.active_supervisors} icon={CheckCircle2} tone="emerald" />
          <Tile label="Captures" v={summary.total_captured} icon={ScanFace} tone="sky" />
          <Tile
            label="Expected"
            v={summary.total_expected}
            icon={Filter}
            tone="amber"
            hint={data?.window_start && data?.window_end ? `${data.window_start} – ${data.window_end} IST` : null}
          />
        </div>

        {/* Grid */}
        <Card>
          <CardHeader className="py-2">
            <CardTitle className="text-sm">
              {data?.date} · {hourRange.length}-hour window
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">
                ({data?.window_start || "08:00"} – {data?.window_end || "18:00"} IST)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
            ) : !filteredRows.length ? (
              <div className="p-8 text-center text-sm text-muted-foreground">
                No supervisors match your filter.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[12px]" data-testid="hourly-grid">
                  <thead>
                    <tr className="border-y bg-slate-50/80 text-left text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="sticky left-0 z-10 min-w-[220px] bg-slate-50/80 px-3 py-2">Supervisor</th>
                      <th className="px-2 py-2 text-right">Captured / Expected</th>
                      {hourRange.map((h) => (
                        <th key={h} className="min-w-[64px] px-1.5 py-2 text-center font-mono">
                          {h.toString().padStart(2, "0")}h
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const captured = row.totals?.captured || 0;
                      const expected = row.totals?.expected || 0;
                      const pct = expected ? Math.min(100, Math.round((captured / expected) * 100)) : 0;
                      const ratioTone = expected === 0
                        ? "text-slate-500"
                        : pct >= 80
                          ? "text-emerald-700"
                          : pct >= 40
                            ? "text-amber-700"
                            : "text-rose-700";
                      return (
                        <tr key={row.id} className="border-b last:border-b-0 hover:bg-slate-50/60">
                          <td className="sticky left-0 z-10 bg-white px-3 py-2">
                            <button
                              type="button"
                              onClick={() => setMapView({ row, focusId: null })}
                              className="text-left font-medium text-slate-800 underline-offset-2 hover:text-sky-700 hover:underline"
                              data-testid={`open-supervisor-map-${row.id}`}
                              title="Open map view"
                            >
                              {row.name}
                            </button>
                            <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
                              <span className="font-mono">{row.username}</span>
                              {row.park_names?.length ? (
                                <span className="inline-flex items-center gap-0.5">
                                  · <MapPin className="h-3 w-3" /> {row.park_names.join(", ")}
                                </span>
                              ) : null}
                              <span className="ml-1">· {row.interval_minutes}m cadence</span>
                            </div>
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums">
                            <span className={`font-semibold ${ratioTone}`}>
                              {captured}/{expected}
                            </span>
                          </td>
                          {hourRange.map((h) => {
                            const list = row.buckets?.[h] || [];
                            if (!list.length) {
                              return (
                                <td key={h} className="px-1.5 py-1.5 text-center">
                                  <span className="inline-block text-[10px] text-slate-300">—</span>
                                </td>
                              );
                            }
                            return (
                              <td key={h} className="px-1 py-1.5 text-center">
                                <div className="flex flex-wrap justify-center gap-0.5">
                                  {list.map((c) => (
                                    <SelfieThumb
                                      key={c.id}
                                      cap={c}
                                      onClick={() => setMapView({ row, focusId: c.id })}
                                    />
                                  ))}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Supervisor map dialog — Leaflet map showing each assigned park's
         geofence circle plus every capture location for the day, with a
         chronological side panel of photos. Opened by clicking the
         supervisor's name OR any thumbnail in the grid (the clicked
         thumbnail is auto-panned to and highlighted). */}
      {mapView ? (
        <SupervisorMapDialog
          row={mapView.row}
          focusId={mapView.focusId}
          date={data?.date || date}
          onClose={() => setMapView(null)}
        />
      ) : null}
    </AppShell>
  );
}

function Tile({ label, v, icon: Icon, tone = "slate", hint }) {
  const tones = {
    slate: "border-slate-200 bg-slate-50 text-slate-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
  };
  return (
    <div className={`flex items-center gap-3 rounded-md border px-3 py-2 ${tones[tone]}`}>
      {Icon ? <Icon className="h-5 w-5" /> : null}
      <div className="flex flex-col">
        <span className="text-[10px] uppercase tracking-wider opacity-80">{label}</span>
        <span className="text-xl font-semibold tabular-nums leading-tight">{v ?? 0}</span>
        {hint ? <span className="text-[10px] text-slate-500">{hint}</span> : null}
      </div>
    </div>
  );
}

function SelfieThumb({ cap, onClick }) {
  const matchPct = cap.face_match_pct;
  const okFace = cap.face_status === "match";
  const noRef = cap.face_status === "no_reference";
  const ring = okFace
    ? "ring-emerald-300"
    : cap.face_status === "mismatch"
      ? "ring-rose-300"
      : noRef
        ? "ring-slate-300"
        : "ring-amber-300";
  const src = cap.photo_b64?.startsWith("data:")
    ? cap.photo_b64
    : `data:image/jpeg;base64,${cap.photo_b64}`;
  const ts = cap.captured_at ? fmtTime(cap.captured_at) : "";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative grid h-12 w-12 place-items-center overflow-hidden rounded-md ring-1 ${ring} hover:ring-2`}
      title={`${ts} · ${cap.park_name || "—"} · ${cap.face_status || "—"}${matchPct != null ? ` (${matchPct}%)` : ""}`}
      data-testid={`selfie-thumb-${cap.id}`}
    >
      <img src={src} alt="" className="h-full w-full object-cover transition group-hover:scale-105" />
      <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-[1px] text-center text-[9px] leading-tight text-white">
        {ts.split(" ")[0] || ts}
      </span>
      {noRef ? (
        <span className="absolute right-0 top-0 grid h-4 w-4 place-items-center rounded-bl bg-slate-700/70 text-[8px] text-white">
          <CircleSlash className="h-2.5 w-2.5" />
        </span>
      ) : null}
    </button>
  );
}


/**
 * Supervisor Map Dialog.
 *
 * Renders an interactive Leaflet map showing:
 *   • One filled circle per assigned park (the configured geofence
 *     boundary) — gives the admin instant visual context of where the
 *     supervisor is supposed to be.
 *   • One numbered marker per capture, plotted at the saved
 *     (gps_lat, gps_long). Markers are colored emerald if the saved
 *     `inside_geofence` is true, rose if false. Hovering a marker shows
 *     a thumbnail + time + park.
 *   • A chronological side panel listing every capture (photo, time,
 *     park, face status, GPS coordinates, Inside/Outside badge).
 *
 * Behaviour:
 *   • Clicking a side-panel row selects that capture, pans the map to
 *     it and highlights both the row and the marker.
 *   • The `focusId` prop (set when the admin clicks a thumbnail in the
 *     hourly grid) preselects that capture on mount.
 *   • Captures with NO GPS (legacy "GPS unknown" rows from before the
 *     mandatory-GPS fix) are not plotted on the map but still appear in
 *     the side list with a clear "GPS unknown" badge so the admin knows
 *     something exists.
 */
/**
 * Distance between two lat/lng points in meters (Haversine).
 */
function haversineMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

/**
 * Offset a lat/lng by a small distance (meters) on a given bearing.
 * Used to "spiderfy" markers that share the same coordinate so each is
 * individually clickable on the map.
 */
function offsetLatLng(lat, lng, meters, bearingDeg) {
  const R = 6378137;
  const br = (bearingDeg * Math.PI) / 180;
  const dN = meters * Math.cos(br);
  const dE = meters * Math.sin(br);
  const dLat = dN / R;
  const dLng = dE / (R * Math.cos((lat * Math.PI) / 180));
  return [lat + (dLat * 180) / Math.PI, lng + (dLng * 180) / Math.PI];
}

/**
 * Helper child that programmatically pans the parent MapContainer when the
 * selected capture changes AND fits initial bounds to all parks + captures
 * on first mount so the admin sees everything relevant at once.
 */
function MapController({ initialBounds, center, zoom }) {
  const map = useMap();
  const didFitRef = React.useRef(false);
  React.useEffect(() => {
    if (didFitRef.current) return;
    if (initialBounds && initialBounds.length >= 1) {
      try {
        if (initialBounds.length === 1) {
          map.setView(initialBounds[0], 15);
        } else {
          map.fitBounds(initialBounds, { padding: [40, 40], maxZoom: 16 });
        }
        didFitRef.current = true;
      } catch (e) {
        // ignore
      }
    }
  }, [initialBounds, map]);
  React.useEffect(() => {
    if (!center) return;
    map.flyTo(center, zoom ?? Math.max(map.getZoom(), 16), { animate: true, duration: 0.5 });
  }, [center, zoom, map]);
  return null;
}

function SupervisorMapDialog({ row, focusId, date, onClose }) {
  // Flatten the supervisor's bucket map into a single chronological list.
  const captures = React.useMemo(() => {
    const all = [];
    const buckets = row?.buckets || {};
    const seenPrimaries = new Set();
    Object.values(buckets).forEach((arr) => {
      (arr || []).forEach((c) => {
        const primaryKey = c.mirror_of || c.id;
        if (!seenPrimaries.has(primaryKey)) {
          seenPrimaries.add(primaryKey);
          all.push(c);
        }
      });
    });
    all.sort((a, b) => {
      const ta = a.captured_at ? new Date(a.captured_at).getTime() : 0;
      const tb = b.captured_at ? new Date(b.captured_at).getTime() : 0;
      return ta - tb;
    });
    return all;
  }, [row]);

  const [selectedId, setSelectedId] = useState(focusId || null);
  useEffect(() => { setSelectedId(focusId || null); }, [focusId]);

  // Captures with usable coordinates (anything else is treated as
  // "GPS unknown" — visible in the list but absent from the map).
  const geoCaptures = captures.filter((c) => c.gps_lat != null && c.gps_long != null);

  // Park geofence rings come straight from the backend payload.
  const parks = React.useMemo(() => row?.park_geofences || [], [row]);

  // ---- Park-mismatch detection -----------------------------------------
  // When a capture's `inside_geofence` is FALSE (i.e. the supervisor was
  // not inside the park they SELECTED while taking the photo), check
  // whether their GPS coordinates land inside any OTHER assigned park's
  // geofence. If so, that usually means the supervisor picked the wrong
  // park in the dropdown — which is far more useful for admin than a
  // bare "Outside" badge.
  const captureMeta = React.useMemo(() => {
    const out = {};
    captures.forEach((c) => {
      if (c.gps_lat == null || c.gps_long == null) return;
      if (c.inside_geofence === false) {
        const actualPark = parks.find((p) =>
          p.latitude != null
            && p.longitude != null
            && haversineMeters([c.gps_lat, c.gps_long], [p.latitude, p.longitude]) <= (p.geofence_radius_m || 500)
        );
        if (actualPark && actualPark.id !== c.park_id) {
          out[c.id] = { wrongParkSelected: true, actualPark };
        }
      }
    });
    return out;
  }, [captures, parks]);

  // ---- Spiderfy overlapping markers ------------------------------------
  // Group captures whose actual GPS distance is <= 15m apart (more
  // reliable than rounding lat/lng strings). For groups of >1 captures,
  // offset every member onto a small ring around the cluster centroid
  // so each marker is individually clickable on the map.
  const spiderfied = React.useMemo(() => {
    const positioned = [];
    captures.forEach((c) => {
      if (c.gps_lat == null || c.gps_long == null) return;
      positioned.push(c);
    });
    // Greedy clustering: assign each capture to the first existing
    // cluster whose centroid is within 15m, otherwise start a new one.
    const clusters = [];
    positioned.forEach((c) => {
      const pt = [c.gps_lat, c.gps_long];
      let placed = false;
      for (const cl of clusters) {
        if (haversineMeters(cl.center, pt) <= 15) {
          cl.members.push(c);
          placed = true;
          break;
        }
      }
      if (!placed) clusters.push({ center: pt, members: [c] });
    });
    const map = {};
    clusters.forEach((cl) => {
      if (cl.members.length === 1) {
        const c = cl.members[0];
        map[c.id] = [c.gps_lat, c.gps_long];
      } else {
        // Spread on a ring of ~35 meters radius around the cluster
        // centroid — visibly distinct at common zoom levels (14-18)
        // while still small enough to feel like a related cluster.
        const radius = 35;
        cl.members.forEach((c, k) => {
          const bearing = (360 * k) / cl.members.length;
          map[c.id] = offsetLatLng(cl.center[0], cl.center[1], radius, bearing);
        });
      }
    });
    return map;
  }, [captures]);

  // ---- Initial bounds: include every park circle + every capture -------
  const initialBounds = React.useMemo(() => {
    const pts = [];
    parks.forEach((p) => {
      if (p.latitude != null && p.longitude != null) {
        // Include the geofence edges so the whole circle is in view.
        const r = (p.geofence_radius_m || 500);
        const dLat = (r / 111320);
        const dLng = (r / (111320 * Math.cos((p.latitude * Math.PI) / 180)));
        pts.push([p.latitude + dLat, p.longitude + dLng]);
        pts.push([p.latitude - dLat, p.longitude - dLng]);
      }
    });
    Object.values(spiderfied).forEach((pt) => pts.push(pt));
    return pts;
  }, [parks, spiderfied]);

  // Compute a sensible fallback center if the bounds list is empty.
  const initialCenter = React.useMemo(() => {
    const firstPark = parks.find((p) => p.latitude != null && p.longitude != null);
    if (firstPark) return [firstPark.latitude, firstPark.longitude];
    if (geoCaptures[0]) return [geoCaptures[0].gps_lat, geoCaptures[0].gps_long];
    return [11.1271, 78.6569]; // Tamil Nadu rough centroid
  }, [parks, geoCaptures]);

  const selectedCapture = captures.find((c) => c.id === selectedId);
  const panTarget = selectedCapture && spiderfied[selectedCapture.id]
    ? spiderfied[selectedCapture.id]
    : null;

  const focusRowRef = React.useRef(null);
  React.useEffect(() => {
    const id = setTimeout(() => {
      if (focusRowRef.current) focusRowRef.current.scrollIntoView({ block: "center" });
    }, 80);
    return () => clearTimeout(id);
  }, [selectedId]);

  // Order captures chronologically and assign a 1-based index so map
  // markers visibly show capture order.
  const indexedCaptures = captures.map((c, i) => ({ ...c, _idx: i + 1 }));

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl p-0" data-testid="supervisor-map-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 pr-10">
          <DialogTitle className="flex flex-wrap items-baseline gap-2 text-base pr-2">
            <span className="font-semibold">{row?.name || "Supervisor"}</span>
            <span className="text-xs font-normal text-muted-foreground">
              {row?.username} · {date}
            </span>
            <span className="ml-auto text-[11px] font-normal text-muted-foreground">
              {captures.length} capture{captures.length === 1 ? "" : "s"}
              {row?.totals?.expected ? ` · expected ${row.totals.expected}` : ""}
              {parks.length ? ` · ${parks.length} park${parks.length === 1 ? "" : "s"}` : ""}
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-0 md:grid-cols-[minmax(0,1fr)_360px]">
          {/* Map column */}
          <div className="relative h-[60vh] min-h-[420px] w-full bg-slate-100 md:rounded-bl-lg">
            <MapContainer
              center={initialCenter}
              zoom={13}
              scrollWheelZoom
              className="h-full w-full md:rounded-bl-lg"
              data-testid="supervisor-map"
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {/* Park geofence circles */}
              {parks.map((p) =>
                p.latitude != null && p.longitude != null ? (
                  <Circle
                    key={p.id}
                    center={[p.latitude, p.longitude]}
                    radius={p.geofence_radius_m || 500}
                    pathOptions={{ color: "#0ea5e9", weight: 1.5, fillColor: "#0ea5e9", fillOpacity: 0.08 }}
                  >
                    <Tooltip>
                      <div className="text-xs">
                        <div className="font-semibold text-slate-800">{p.name}</div>
                        <div className="text-muted-foreground">
                          {p.code} · {p.geofence_radius_m || 500} m geofence
                        </div>
                      </div>
                    </Tooltip>
                  </Circle>
                ) : null,
              )}
              {/* Capture markers — numbered, colored by inside_geofence */}
              {indexedCaptures.map((c) => {
                if (c.gps_lat == null || c.gps_long == null) return null;
                const isInside = c.inside_geofence === true;
                const isOutside = c.inside_geofence === false;
                const isSelected = c.id === selectedId;
                const meta = captureMeta[c.id];
                const color = isInside ? "#059669" : isOutside ? "#e11d48" : "#64748b";
                const numberIcon = L.divIcon({
                  className: "",
                  html: `<div style="
                    background:${color};
                    color:white;
                    width:26px;height:26px;border-radius:50%;
                    display:flex;align-items:center;justify-content:center;
                    font-size:12px;font-weight:600;
                    border:${isSelected ? "3px solid #f59e0b" : "2px solid white"};
                    box-shadow:0 1px 4px rgba(0,0,0,.4);
                  ">${c._idx}</div>`,
                  iconSize: [26, 26],
                  iconAnchor: [13, 13],
                });
                const displayPos = spiderfied[c.id] || [c.gps_lat, c.gps_long];
                return (
                  <Marker
                    key={c.id}
                    position={displayPos}
                    icon={numberIcon}
                    eventHandlers={{
                      click: () => setSelectedId(c.id),
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -10]}>
                      <div className="flex items-center gap-2">
                        <img
                          src={c.photo_b64?.startsWith("data:") ? c.photo_b64 : `data:image/jpeg;base64,${c.photo_b64}`}
                          alt=""
                          className="h-12 w-12 rounded object-cover ring-1 ring-slate-200"
                        />
                        <div className="text-[11px] leading-tight">
                          <div className="font-mono font-semibold">
                            #{c._idx} · {c.captured_at ? fmtTime(c.captured_at) : "—"}
                          </div>
                          <div className="text-muted-foreground">Selected: {c.park_name || "—"}</div>
                          <div>
                            {isInside ? "Inside selected park" : isOutside ? "Outside selected park" : "GPS unknown"}
                          </div>
                          {meta?.wrongParkSelected ? (
                            <div className="text-rose-700">
                              ⚠ GPS actually inside {meta.actualPark.name}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </Tooltip>
                  </Marker>
                );
              })}
              <MapController initialBounds={initialBounds.length ? initialBounds : null} center={panTarget} />
            </MapContainer>
            {/* Legend */}
            <div className="pointer-events-none absolute bottom-2 left-2 z-[400] rounded-md bg-white/95 px-2 py-1 text-[10px] shadow ring-1 ring-slate-200">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#0ea5e9", opacity: 0.5 }}></span>
                Park geofence
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#059669" }}></span>
                Inside geofence
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#e11d48" }}></span>
                Outside geofence
              </div>
            </div>
          </div>
          {/* Side panel: chronological capture list */}
          <div
            className="flex max-h-[60vh] min-h-[420px] flex-col gap-2 overflow-y-auto border-t border-slate-200 p-2 md:max-h-[60vh] md:border-l md:border-t-0"
            data-testid="supervisor-map-list"
          >
            {captures.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No captures recorded on this date.
              </div>
            ) : (
              indexedCaptures.map((c) => {
                const isFocus = c.id === selectedId;
                const meta = captureMeta[c.id];
                const src = c.photo_b64?.startsWith("data:")
                  ? c.photo_b64
                  : `data:image/jpeg;base64,${c.photo_b64}`;
                const inside = c.inside_geofence;
                const faceTone = c.face_status === "match"
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  : c.face_status === "mismatch"
                    ? "bg-rose-50 text-rose-700 ring-rose-200"
                    : c.face_status === "no_face"
                      ? "bg-amber-50 text-amber-700 ring-amber-200"
                      : "bg-slate-100 text-slate-600 ring-slate-200";
                return (
                  <button
                    type="button"
                    key={c.id}
                    ref={isFocus ? focusRowRef : null}
                    onClick={() => setSelectedId(c.id)}
                    className={`flex items-stretch gap-2 rounded-md border p-2 text-left transition ${
                      isFocus ? "bg-amber-50/70 ring-2 ring-amber-300" : "bg-white hover:bg-slate-50"
                    }`}
                    data-testid={`day-capture-${c.id}`}
                  >
                    <div className="relative flex-none">
                      <img
                        src={src}
                        alt=""
                        className="h-20 w-20 rounded object-cover ring-1 ring-slate-200"
                      />
                      <span className="absolute -left-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-slate-900/85 text-[10px] font-semibold text-white ring-1 ring-white">
                        {c._idx}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-1.5 text-sm">
                        <Clock className="h-3.5 w-3.5 text-slate-500" />
                        <span className="font-mono text-[12px] font-semibold text-slate-800">
                          {c.captured_at ? fmtTime(c.captured_at) : "—"}
                        </span>
                        {c.park_name ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-[1px] text-[10px] text-slate-700">
                            <MapPin className="h-3 w-3" /> {c.park_name}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-1">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[10px] ring-1 ${faceTone}`}
                          title="Face recognition result"
                        >
                          Face: {c.face_status || "—"}
                          {c.face_match_pct != null ? ` · ${c.face_match_pct}%` : ""}
                        </span>
                        {inside === true ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-[1px] text-[10px] text-emerald-700 ring-1 ring-emerald-200"
                          >
                            <ShieldCheck className="h-3 w-3" /> Inside
                          </span>
                        ) : inside === false ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-[1px] text-[10px] text-rose-700 ring-1 ring-rose-200"
                          >
                            <ShieldAlert className="h-3 w-3" /> Outside
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-[1px] text-[10px] text-slate-600 ring-1 ring-slate-200"
                          >
                            <ShieldAlert className="h-3 w-3" /> GPS unknown
                          </span>
                        )}
                      </div>
                      {c.gps_lat != null && c.gps_long != null ? (
                        <a
                          className="text-[10px] text-sky-700 underline-offset-2 hover:underline"
                          href={`https://maps.google.com/?q=${c.gps_lat},${c.gps_long}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`day-capture-gps-${c.id}`}
                        >
                          {c.gps_lat.toFixed?.(5)}, {c.gps_long.toFixed?.(5)}
                        </a>
                      ) : null}
                      {meta?.wrongParkSelected ? (
                        <div
                          className="mt-0.5 rounded-md bg-rose-50 px-1.5 py-1 text-[10px] leading-tight text-rose-800 ring-1 ring-rose-200"
                          data-testid={`wrong-park-warn-${c.id}`}
                        >
                          ⚠ Wrong park selected — supervisor picked
                          <span className="font-semibold"> {c.park_name}</span>
                          {" "}but their GPS is inside
                          <span className="font-semibold"> {meta.actualPark.name}</span>
                          {"'"}s geofence.
                        </div>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
